import { Severity, captureMessage } from '@sentry/nextjs';

import Header from '@/components/Header';
import PageHead from '@/components/PageHead/PageHead';
import PageLayout from '@/components/PageLayout';
import Sidebar from '@/components/Sidebar';
import { DAPP_PAGES } from '@/constants/constants';
import {
  SENTRY_TAGS,
  SENTRY_TAGS_TYPE_VALUES,
} from '@/constants/monitoring.constants';
import DeFiPage from '@/container/pages/Defi';
import { useDappSDKProvider } from '@/dapp-sdk-v2';
import { env } from '@/env';
import { createCmsURLs } from '@/factories/url.factory';
import { CMSDataProvider } from '@/providers/CMSDataProvider';
import { DefiPageData, DefiPageResponse } from '@/types/cms/defi-page.types';
import {
  StakingPageGeneralData,
  StakingPageGeneralResponse,
} from '@/types/cms/staking-page-general.type';
import {
  StakingPageData,
  StakingPageResponse,
} from '@/types/cms/staking-page.types';
import { getPageRedirect } from '@/utils/page.utils';

interface DefiPageProps {
  data: {
    page: StakingPageData;
    general: StakingPageGeneralData;
    defi: DefiPageData;
  };
  error: {
    page: unknown | null;
    general: unknown | null;
    defi: unknown | null;
  };
}

const DefiPage: React.FC<DefiPageProps> = ({ data }) => {
  const { isBannerVisible, setStakeData } = useDappSDKProvider();
  const setMobileMenuVisible = (isVisible: boolean) => {
    setStakeData({
      isMobileMenuOpen: isVisible,
    });
  };

  return (
    <CMSDataProvider
      page={data.page}
      defi={data.defi}
      stakeGeneral={data.general}
    >
      <PageHead page="defi">
        <PageLayout
          Sidebar={
            <Sidebar
              display={{ base: 'none', xl: 'block' }}
              minH={{
                base: '100vh',
                xl: isBannerVisible ? 'calc(100vh - 32px)' : '100vh',
              }}
            />
          }
          MobileSideBar={
            <Sidebar setMobileMenuVisible={setMobileMenuVisible} />
          }
          Header={<Header />}
        >
          <DeFiPage />
        </PageLayout>
      </PageHead>
    </CMSDataProvider>
  );
};

export async function getStaticProps(): Promise<
  { props: DefiPageProps } | { notFound: boolean }
> {
  const pageRedirect = getPageRedirect(DAPP_PAGES.DEFI, env.NEXT_PUBLIC_TOKEN);

  if (pageRedirect != null) {
    return pageRedirect;
  }

  const strapiClient = createCmsURLs();

  const [liquidStakingPage, stakingPageGeneral, defiPageResponse] =
    await Promise.all([
      strapiClient.liquidStakingPage.get().json<StakingPageResponse>(),
      strapiClient.stakingPageGeneral.get().json<StakingPageGeneralResponse>(),
      strapiClient.defiPage.get().json<DefiPageResponse>(),
    ]);

  if (defiPageResponse == null || liquidStakingPage == null) {
    captureMessage('failed to fetch defi page', {
      tags: {
        [SENTRY_TAGS.TYPE]: SENTRY_TAGS_TYPE_VALUES.CMS,
        [SENTRY_TAGS.OPERATION]: 'getStaticProps',
      },
      level: Severity.Error,
    });

    throw new Error('Failed to fetch defi page');
  }

  const stakingData = liquidStakingPage.data.attributes;
  const stakingGeneralData = stakingPageGeneral.data.attributes;
  const defiData = defiPageResponse.data[0].attributes;

  return {
    props: {
      data: {
        page: stakingData,
        general: stakingGeneralData,
        defi: defiData,
      },
      error: {
        page: liquidStakingPage.error ?? null,
        general: stakingPageGeneral?.error ?? null,
        defi: defiPageResponse.error ?? null,
      },
    },
  };
}

export default DefiPage;
